import { nextDefaultRouteName } from "./routeDefaultName";

export interface RouteRow {
  id?: string;
  name?: string;
  condition?: string;
}

const ROUTE_KEYS = ["id", "name", "condition"] as const;
const MIN_ROUTES = 2;

export function serializeRoutes(rows: RouteRow[]): string {
  return JSON.stringify(
    rows.map((row) => ({
      id: row.id ?? "",
      name: row.name ?? "",
      condition: row.condition ?? "",
    })),
    null,
    2
  );
}

export function parseRoutesJson(
  jsonValue: string,
  newId: () => string
): { routes: RouteRow[]; errors: string[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonValue);
  } catch (error) {
    return {
      routes: [],
      errors: [`Invalid JSON: ${(error as Error).message}`],
    };
  }

  if (!Array.isArray(parsed)) {
    return { routes: [], errors: ["Value must be an array of route objects"] };
  }

  if (parsed.length < MIN_ROUTES) {
    return {
      routes: [],
      errors: [`At least ${MIN_ROUTES} routes are required`],
    };
  }

  const errors: string[] = [];
  const seenIds = new Set<string>();
  const usedNames: string[] = [];
  const routes: RouteRow[] = [];

  parsed.forEach((entry, index) => {
    const label = `Route ${index + 1}`;

    if (!entry || typeof entry !== "object") {
      errors.push(`${label}: Must be an object`);
      return;
    }

    const obj = entry as Record<string, unknown>;

    for (const key of Object.keys(obj)) {
      if (!ROUTE_KEYS.includes(key as (typeof ROUTE_KEYS)[number])) {
        errors.push(
          `${label}: Unknown key "${key}". Allowed keys: ${ROUTE_KEYS.join(", ")}`
        );
      }
    }

    for (const key of ROUTE_KEYS) {
      if (!(key in obj)) {
        errors.push(`${label}: Missing required key "${key}"`);
        continue;
      }
      if (typeof obj[key] !== "string") {
        errors.push(`${label}: "${key}" must be a string`);
      }
    }

    if (errors.some((message) => message.startsWith(label))) {
      return;
    }

    let id = (obj.id as string).trim();
    if (!id) {
      id = newId();
    }
    if (seenIds.has(id)) {
      errors.push(`${label}: Duplicate id "${id}"`);
      return;
    }
    seenIds.add(id);

    let name = (obj.name as string).trim();
    if (!name) {
      name = nextDefaultRouteName(usedNames);
    }
    usedNames.push(name);

    routes.push({
      id,
      name,
      condition: obj.condition as string,
    });
  });

  if (errors.length > 0) {
    return { routes: [], errors };
  }

  return { routes, errors: [] };
}
